import {
  Component,
  createEffect,
  createSignal,
  For,
  mergeProps,
  onCleanup,
  Show,
} from 'solid-js';
import {
  AUTOCOMPLETE_DEFAULTS,
  AutoCompleteItem,
  AutoCompleteProps,
} from './AutoComplete.types';

export * from './AutoComplete.types';

// 2025/10/01: This is a temporary component.
// The long term plan is to use either the Connect or Modus2 Autocomplete
export const AutoComplete: Component<Partial<AutoCompleteProps>> = (
  props: Partial<AutoCompleteProps>
) => {
  let autoCompleteRef!: HTMLDivElement;
  let containerRef!: HTMLDivElement;
  let inputElement: HTMLInputElement | undefined;

  const activeProps = mergeProps(AUTOCOMPLETE_DEFAULTS, props);
  const [showOptions, setShowOptions] = createSignal<boolean>(false);
  const [filteredItems, setFilteredItems] = createSignal<AutoCompleteItem[]>();
  const [selected, setSelected] = createSignal<AutoCompleteItem | undefined>(
    undefined
  );

  createEffect(() => {
    if (showOptions()) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  });

  const filterMenuItems = (filterOverride: string | null = null): void => {
    const searchText = inputElement?.value.toLowerCase() ?? '';
    setFilteredItems(
      activeProps.menuItems.filter((x) =>
        x.label.toLowerCase().includes(filterOverride ?? searchText)
      )
    );
  };

  const clear = (): void => {
    setSelected(undefined);
    if (inputElement) {
      inputElement.value = '';
      inputElement.focus();
    }
    filterMenuItems('');
    fireTextChangeEvent();
    fireSelectEvent();
    setShowOptions(true);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    const isInside = (event: MouseEvent, itm: DOMRect): boolean => {
      return (
        event.clientX >= itm.left &&
        event.clientX <= itm.right &&
        event.clientY >= itm.top &&
        event.clientY <= itm.bottom
      );
    };

    const hasClickedInsideDropdown = isInside(
      event,
      autoCompleteRef.getBoundingClientRect()
    );
    const hasClickedInsideTextBox = isInside(
      event,
      containerRef.getBoundingClientRect()
    );

    if (hasClickedInsideDropdown || hasClickedInsideTextBox) {
      return;
    }

    if (inputElement)
      inputElement.value = selected()?.label ?? activeProps.text;

    setShowOptions(false);
  };

  const handleSelect = (item: AutoCompleteItem): void => {
    setSelected({
      label: item.label,
      value: item.value,
    });

    if (inputElement) inputElement.value = item.label;
    setShowOptions(false);
    fireSelectEvent();
  };

  const fireTextChangeEvent = (): void => {
    if (activeProps.onTextChange) {
      activeProps.onTextChange({
        text: inputElement?.value ?? '',
      });
    }
  };

  const fireSelectEvent = (): void => {
    if (activeProps.onItemSelect) {
      activeProps.onItemSelect({
        item: selected(),
      });
    }
  };

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
  });

  return (
    <div
      class="relative w-full"
      ref={containerRef}
      data-testid={activeProps.name ? `autocomplete-${activeProps.name}` : null}
    >
      <div class="relative">
        <input
          spellcheck={false}
          autocomplete="false"
          ref={inputElement}
          data-testid={
            activeProps.name ? `autocomplete-text-${activeProps.name}` : null
          }
          type="text"
          class={`${props.noBorder ? '' : 'border-[1px] border-solid border-gray-6'} rounded-[4px] text-[0.75rem] text-gray-8 w-full`}
          onChange={() => {
            fireTextChangeEvent();
          }}
          value={activeProps.text}
          onKeyUp={() => {
            filterMenuItems();
          }}
          onClick={() => {
            inputElement?.select();
            filterMenuItems('');
            setShowOptions(true);
          }}
          onFocus={() => {
            filterMenuItems('');
            setShowOptions(true);
          }}
          ondblclick={() => {
            inputElement?.select();
          }}
        />

        <span
          class="cursor-pointer absolute top-[50%] right-6 translate-y-[-50%]"
          data-testid={
            activeProps.name ? `autocomplete-clear-${activeProps.name}` : null
          }
        >
          <modus-wc-icon
            name="close"
            attr:custom-class="!flex"
            size="xs"
            onClick={() => {
              clear();
            }}
          />
        </span>

        <span class="cursor-pointer absolute top-[50%] right-2 translate-y-[-50%] select-none">
          <modus-wc-icon
            name={!showOptions() ? 'expand_more' : 'expand_less'}
            attr:custom-class="!flex"
            size="xs"
            onClick={() => {
              if (!showOptions()) {
                filterMenuItems('');
                setShowOptions(true);
              } else {
                setShowOptions(false);
              }
            }}
          />
        </span>
      </div>
      <Show when={showOptions()}>
        <div
          data-testid={
            activeProps.name
              ? `autocomplete-dropdown-${activeProps.name}`
              : null
          }
          ref={autoCompleteRef}
          class={`bg-white w-full border border-gray-1 border-solid absolute overflow-y-auto mt-2`}
          style={`height: ${activeProps.height.toString()}px`}
        >
          <For each={filteredItems()}>
            {(item, _) => (
              <div
                class="pl-2 pt-1 pb-1 hover:bg-gray-light hover:cursor-pointer"
                onClick={() => {
                  handleSelect(item);
                }}
              >
                {item.label}
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

export default AutoComplete;
